/**
 * Password Validation Utility
 *
 * Client-side validation for password strength.
 * Note: Breach checks and contextual checks are performed server-side only.
 */

export interface PasswordValidationResult {
  valid: boolean;
  error: string | null;
}

/**
 * Validate password against basic requirements:
 * - At least 8 characters
 * - At most 64 characters
 * - At least 1 letter
 * - At least 1 number
 */
export function validatePassword(password: string): PasswordValidationResult {
  // Check minimum length
  if (password.length < 8) {
    return {
      valid: false,
      error: 'Password must be at least 8 characters and contain a letter and a number.',
    };
  }

  // Check maximum length
  if (password.length > 64) {
    return {
      valid: false,
      error: 'Password must be at least 8 characters and contain a letter and a number.',
    };
  }

  // Check for at least one letter (a-z or A-Z)
  if (!/[a-zA-Z]/.test(password)) {
    return {
      valid: false,
      error: 'Password must be at least 8 characters and contain a letter and a number.',
    };
  }

  // Check for at least one number
  if (!/[0-9]/.test(password)) {
    return {
      valid: false,
      error: 'Password must be at least 8 characters and contain a letter and a number.',
    };
  }

  return { valid: true, error: null };
}

/**
 * Get helper text for password input field
 */
export function getPasswordHelperText(): string {
  return 'At least 8 characters with a letter and a number';
}
